// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// Transparent at-rest encryption for device private keys. The armored private key in each
// `zalmoxis.key.<userId>/<deviceId>` slot is sealed with a **non-extractable** AES-GCM key that
// lives in IndexedDB: the browser can USE it to decrypt (so the engine loads keys on refresh with
// no passphrase) but JS can never read its raw bytes, so a localStorage dump / backup / devtools
// peek only ever yields ciphertext, and the key itself can't be exfiltrated.
//
// Recovery: the WebCrypto key never leaves this browser profile, so if it's lost (cleared site
// data, a new browser, eviction) the local ciphertext is undecryptable — but the user's encrypted
// SERVER key backup re-creates the local store. The WebCrypto key is the frictionless cache; the
// backup is the durable source of truth.
//
// EVERY user is covered: where WebCrypto.subtle or IndexedDB is unavailable (insecure context,
// ancient browser) the seal is a no-op and keys fall back to plaintext slots — exactly today's
// behaviour, never a hard failure. Opt-in passphrase keylock + (future) WebAuthn unlock layer on
// top of this foundation.

const DB_NAME = "zalmoxis-keystore";
const STORE = "wrap";
const KEY_ID = "device-key-wrap-v1";
// Sealed-value sentinels (anything else is legacy plaintext): v1 = automatic non-extractable browser
// key; wa1 = WebAuthn PRF-derived key (Phase 2 step-up, only usable after a YubiKey/biometric tap).
const MARK_V1 = "enc:v1:";
const MARK_WA = "enc:wa1:";

// Step-up state (in-memory): the PRF-derived key after a successful tap, and which key NEW seals use.
let webauthnKey: CryptoKey | null = null;
let sealMode: "auto" | "webauthn" = "auto";

// setWebauthnKey caches the PRF-derived sealing key for the session (null on lock/forget); it's used
// to OPEN `enc:wa1:` slots regardless of sealMode.
export function setWebauthnKey(key: CryptoKey | null): void {
  webauthnKey = key;
}
// setSealMode picks which key NEW seals (sealPriv) use.
export function setSealMode(mode: "auto" | "webauthn"): void {
  sealMode = mode;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbGet(key: string): Promise<unknown> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const r = db.transaction(STORE, "readonly").objectStore(STORE).get(key);
        r.onsuccess = () => resolve(r.result);
        r.onerror = () => reject(r.error);
      }),
  );
}

function idbPut(key: string, val: unknown): Promise<void> {
  return openDB().then(
    (db) =>
      new Promise((resolve, reject) => {
        const r = db.transaction(STORE, "readwrite").objectStore(STORE).put(val, key);
        r.onsuccess = () => resolve();
        r.onerror = () => reject(r.error);
      }),
  );
}

// undefined = not resolved yet · null = unavailable (fallback to plaintext) · CryptoKey = ready.
let cached: CryptoKey | null | undefined;

// wrapKey returns the browser's non-extractable wrapping key, creating + persisting it on first
// use, or null when the platform can't provide one (so callers fall back to plaintext).
async function wrapKey(): Promise<CryptoKey | null> {
  if (cached !== undefined) {
    return cached;
  }
  try {
    if (typeof indexedDB === "undefined" || !globalThis.crypto?.subtle) {
      cached = null;
      return null;
    }
    let key = (await idbGet(KEY_ID)) as CryptoKey | undefined;
    if (!key) {
      key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, /* extractable */ false, ["encrypt", "decrypt"]);
      await idbPut(KEY_ID, key);
      // Ask the browser not to evict our storage under disk pressure.
      void navigator.storage?.persist?.().catch(() => undefined);
    }
    cached = key;
    return key;
  } catch {
    cached = null;
    return null;
  }
}

function b64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function unb64(s: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

// keystoreAvailable reports whether at-rest sealing is active (false ⇒ plaintext fallback).
export async function keystoreAvailable(): Promise<boolean> {
  return (await wrapKey()) !== null;
}

async function encWith(key: CryptoKey, mark: string, priv: string): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, Uint8Array.from(new TextEncoder().encode(priv)));
  return mark + b64(iv) + ":" + b64(new Uint8Array(ct));
}

async function decWith(key: CryptoKey, stored: string): Promise<string> {
  const p = stored.split(":");
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(p[2]) }, key, unb64(p[3]));
  return new TextDecoder().decode(pt);
}

// sealPriv encrypts an armored private key for storage under the ACTIVE seal mode: the WebAuthn
// PRF key (step-up) or the automatic browser key. Already-sealed / empty input passes through; with
// no automatic wrapping key it returns plaintext unchanged (fallback for unsupported platforms).
export async function sealPriv(priv: string): Promise<string> {
  if (!priv || isSealed(priv)) {
    return priv;
  }
  if (sealMode === "webauthn") {
    if (!webauthnKey) {
      throw new Error("security key locked — tap to unlock before saving keys");
    }
    return encWith(webauthnKey, MARK_WA, priv);
  }
  const key = await wrapKey();
  if (!key) {
    return priv;
  }
  return encWith(key, MARK_V1, priv);
}

// openPriv decrypts a stored value by its sentinel (browser key vs WebAuthn key). Legacy plaintext
// passes through, so old slots keep working until re-sealed. Throws if a sealed value can't be
// opened (lost browser key, or the security key hasn't been tapped this session).
export async function openPriv(stored: string): Promise<string> {
  if (!stored) {
    return stored;
  }
  if (stored.startsWith(MARK_WA)) {
    if (!webauthnKey) {
      throw new Error("locked — tap your security key to unlock");
    }
    return decWith(webauthnKey, stored);
  }
  if (stored.startsWith(MARK_V1)) {
    const key = await wrapKey();
    if (!key) {
      throw new Error("keystore key unavailable — cannot decrypt sealed key");
    }
    return decWith(key, stored);
  }
  return stored; // legacy plaintext
}

// isSealed reports whether a stored priv value is encrypted (either mode) vs legacy plaintext.
export function isSealed(stored: string): boolean {
  return stored.startsWith(MARK_V1) || stored.startsWith(MARK_WA);
}

// resealUser re-encrypts every one of the user's device-key slots from its current sentinel to the
// active seal mode — used when switching tiers (enable/disable the WebAuthn step-up). Both keys must
// be available: the source key to open each slot, the target (sealMode) key to re-seal it.
export async function resealUser(userId: string): Promise<void> {
  const prefix = `zalmoxis.key.${userId}/`;
  const keys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith(prefix)) keys.push(k);
  }
  for (const k of keys) {
    try {
      const slot = JSON.parse(localStorage.getItem(k) || "{}");
      if (!slot.priv) continue;
      const priv = await openPriv(slot.priv);
      localStorage.setItem(k, JSON.stringify({ ...slot, priv: await sealPriv(priv) }));
    } catch {
      /* skip malformed slot */
    }
  }
}
